<script context="module">
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2025 (original work) Open Assessment Technologies SA ;

    let cookiePolicyInitialized = false;
</script>

<script>
    import { __, stringToSha256 } from '@oat-sa-private/ui-core';
    import { Switch, Button } from '@oat-sa-private/ui-elements';
    import Notification from '../notification/Notification.svelte';
    import ModalOverlay from '../modal/ModalOverlay.svelte';
    import localStorageProvider from "./localStorageProvider.js";

    import { onMount, createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();
    /**
     * @property {string} storageId - A string containing id to identify app that using cookie policy modal
     * @property {string} title - A string containing title for cookie policy banner
     * @property {string} message - A string containing message for cookie policy banner
     * @property {Array} options - An array of options to be displayed in the modal
     * @property {object} storage - Custom storage object. Defaults to `localStorage`.
     * Must implement `getItem`, `setItem`, and `removeItem` methods if overridden.
     */

    export let storageId = null;
    export let storage = localStorageProvider;
    export let title = __('Cookie Policy');
    export let message = __(
        'This site uses cookies to improve your browsing experience and to perform analytics and research. To change your preferences, click Manage preferences. Otherwise, clicking Accept all cookies indicates you agree to our use of cookies on your device. Clicking Reject all cookies means you do not agree to our use of non-strictly necessary cookies on your device.'
    );
    export let options = [];

    let showCookiePolicy = false;
    let modalOpen = false;

    let actions = [
        {
            type: 'button-link',
            title: __('Manage Preferences'),
            action: 'manage'
        },
        {
            type: 'button',
            title: __('Reject All'),
            action: 'reject',
            skin: 'secondary'
        },
        {
            type: 'button',
            title: __('Accept All'),
            action: 'accept'
        }
    ];
    let storageKey = null;

    $: extendedOptions = options.map(item => ({
        ...item,
        enabled: item.enabled ?? true
    }));
    
/**
     * Handles user interactions with the cookie policy banner (accept, reject, manage, etc.).
     *
     * Depending on the user's choice, this function stores preferences in `storage`,
     * dispatches relevant events, and updates UI state (e.g., hides the banner, opens modal).
     *
     * @param {CustomEvent} event - The event object containing action details.
     * @param {'accept'|'reject'|'accept-selected'|'manage'} event.detail.action - The specific user action.
     */
    function handleAction(event) {
        let simplifiedOptions = extendedOptions.reduce((acc, item) => {
            acc[item.value] = item.enabled;
            return acc;
        }, {});
        switch (event.detail.action) {
            case 'accept':
                storage.setItem(storageKey, simplifiedOptions);
                dispatch('initializeCookies', simplifiedOptions);
                hideCookiePolicyBanner();
                break;
            case 'reject':
                if (extendedOptions.some(item => item.required)) {
                    for (const item of extendedOptions) {
                        simplifiedOptions[item.value] = Boolean(item.required);
                    }
                    storage.setItem(storageKey, simplifiedOptions);
                    dispatch('initializeCookies', simplifiedOptions);
                } else {
                    storage.setItem(storageKey, false);
                    dispatch('cookieReject');
                }
                hideCookiePolicyBanner();
                break;
            case 'accept-selected':
                if (extendedOptions.some(item => item.enabled)) {
                    storage.setItem(storageKey, simplifiedOptions);
                    dispatch('initializeCookies', simplifiedOptions);
                } else {
                    storage.setItem(storageKey, false);
                    dispatch('cookieReject');
                }
                modalOpen = false;
                hideCookiePolicyBanner();
                break;
            case 'manage':
                modalOpen = true;
                break;
        }
    }

    /**
     * Hides the cookie policy banner and marks it as initialized.
     * Used internally after a user makes a cookie preference decision.
     */
    function hideCookiePolicyBanner() {
        showCookiePolicy = false;
        cookiePolicyInitialized = true;
    }

    onMount(async () => {
        stringToSha256(storageId).then(hash => {
            storageKey = `CookiePolicy-${hash}`;
            const allowedCookies = storage.getItem(storageKey);
            if (allowedCookies !== null) {
                if (allowedCookies && !cookiePolicyInitialized) {
                    dispatch('initializeCookies', allowedCookies);
                }
                cookiePolicyInitialized = true;
            } else {
                showCookiePolicy = true;
            }
        });
    });
</script>

<style>
    .cookie-policy-message {
        padding-bottom: var(--space-1x5);
    }

    .cookie-policy-additional-text {
        display: flex;
        align-items: center;
        padding-bottom: var(--space-1x5);
    }

    .cookie-policy-underline {
        border-bottom: 4px solid var(--color-brand);
    }

    .cookie-modal-container {
        background-color: var(--color-bg-default);
        border: var(--border-thin) solid var(--color-text-default);

        position: relative;
        overflow: auto;
        z-index: var(--layer-5);

        /* larger than mobile */
        min-width: 50rem;
        max-width: 75rem;
        max-height: 82rem;

        display: flex;
        flex-direction: column;
        justify-content: flex-between;
        align-items: stretch;

        &:focus,
        &:active {
            outline: none;
        }

        /* mobile */
        @media screen and (--mq-maxwidth-small) {
            width: unset;
            height: unset;
            min-width: unset;
            max-width: calc(100% - 4rem);
            max-height: calc(100vh - 4rem);
        }

        & .header-container {
            padding: 2.5rem 2.5rem 0 2.5rem;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            flex: 3rem 1 1;
            gap: var(--space-2x5);

            & h3 {
                margin: 0;
                padding: 0;
                height: 3rem;
                line-height: 3rem;
            }
        }

        & .content-container {
            padding: 2.5rem 2.5rem 0 2.5rem;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            flex: auto 1 1;
            gap: var(--space-2x5);
            overflow-y: auto;
        }
        & .cookie-options-wrapper {
            display: flex;
            flex-direction: column;
            gap: var(--space-2x);

            & .cookie-option-item {
                display: flex;
                justify-content: space-between;
                font-weight: 600;
            }
        }

        & .actions-container {
            display: flex;
            justify-content: flex-end;
            flex: 9rem 0 0;
            gap: var(--space-2x);
            padding: var(--space-2x);
        }
    }
</style>

{#if showCookiePolicy}
    <Notification {title} {actions} showIcon={true} on:notificationclick={handleAction}>
        <div class="cookie-policy-content" slot="notification-content">
            <div class="cookie-policy-message">{message}</div>
            {#if $$slots['additional-text']}
                <div class="cookie-policy-additional-text">
                    <slot name="additional-text" />
                </div>
            {/if}
        </div>
    </Notification>
    <div class="cookie-policy-underline"></div>
    {#if modalOpen}
        <ModalOverlay>
            <div class="cookie-modal-container">
                <div class="header-container">
                    <h3>{__('Manage Preferences')}</h3>
                    <div>
                        {__(
                            'Customize your cookie preferences. You can choose which types of cookies you want to allow to improve your experience.'
                        )}
                    </div>
                </div>
                <div class="content-container">
                    <div class="cookie-options-wrapper">
                        {#each extendedOptions as option}
                            <div class="cookie-option-item">
                                <div>{option.label}</div>
                                <div>
                                    {#if option.required}
                                        {__('Always Active')}
                                    {:else}
                                        <Switch
                                            value={option.enabled}
                                            ariaLabel={option.label}
                                            on:change={event => option.enabled = event.detail} />
                                    {/if}
                                </div>
                            </div>
                            <div class="cookie-option-description">
                                {option.description}
                            </div>
                        {/each}
                    </div>
                </div>
                <div class="actions-container">
                    <Button
                        label="Confirm Choices"
                        shape="pill"
                        size="small"
                        skin="primary"
                        on:click={() => {
                            handleAction({ detail: { action: 'accept-selected' } });
                        }} />
                </div>
            </div>
        </ModalOverlay>
    {/if}
{/if}
